import { Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { PHOTO_SIZES, PHOTOS, type Photo } from "./images";
import { BOOKING_URL, ROOMS_PATH, routeTo } from "@/lib/site";

type Option = {
  title: string;
  body: string;
  photo: Photo;
  span: string;
  ratio: string;
  sizes: string;
};

const OPTIONS: Option[] = [
  {
    title: "Full-Hookup RV Sites",
    body: "Spacious pull-through and back-in sites with 30- and 50-amp options, water, sewer, electric service, and select patio or covered sites.",
    photo: PHOTOS.rvSite,
    span: "lg:col-span-7",
    ratio: "aspect-[16/10]",
    sizes: PHOTO_SIZES.gridWide,
  },
  {
    title: "Cabins and Park Models",
    body: "Comfortable furnished accommodations for travelers who want more space and the conveniences of home.",
    photo: PHOTOS.parkModelPatio,
    span: "lg:col-span-5",
    ratio: "aspect-[16/10] lg:aspect-[4/3]",
    sizes: PHOTO_SIZES.gridNarrow,
  },
  {
    title: "Motel and Efficiency Rooms",
    body: "Practical furnished rooms with kitchen essentials for weekend visits, fishing trips, work assignments, and extended stays.",
    photo: PHOTOS.cabinRow,
    span: "lg:col-span-4",
    ratio: "aspect-[4/3]",
    sizes: PHOTO_SIZES.gridThird,
  },
  {
    title: "Tent Camping",
    body: "A relaxed option for guests who want to sleep under the South Texas sky while remaining close to campground conveniences.",
    photo: PHOTOS.coveredParking,
    span: "lg:col-span-4",
    ratio: "aspect-[4/3]",
    sizes: PHOTO_SIZES.gridThird,
  },
  {
    title: "Extended and Corporate Stays",
    body: "Flexible options for snowbirds, work crews, energy workers, groups, and seasonal guests.",
    photo: PHOTOS.parkModelCarport,
    span: "lg:col-span-4",
    ratio: "aspect-[4/3]",
    sizes: PHOTO_SIZES.gridThird,
  },
];

export function AccommodationGrid() {
  return (
    <section
      className="bg-surface py-24 sm:py-28 lg:py-32"
      aria-labelledby="stays-title"
      id="stays"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <p className="eyebrow text-clay">Accommodations</p>
          <h2
            id="stays-title"
            className="mt-4 font-display text-3xl leading-tight sm:text-4xl lg:text-[2.9rem]"
          >
            Find the Stay That Fits Your Trip
          </h2>
        </Reveal>

        <ul className="mt-14 grid gap-x-8 gap-y-14 lg:grid-cols-12">
          {OPTIONS.map((option, i) => (
            <li key={option.title} className={option.span}>
              <Reveal delay={(i % 3) * 0.08} className="group h-full">
                <article className="flex h-full flex-col">
                  <Link
                    to={routeTo(ROOMS_PATH)}
                    className="media-zoom block rounded-sm"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <img
                      src={option.photo.src}
                      srcSet={option.photo.srcSet}
                      sizes={option.sizes}
                      alt={option.photo.alt}
                      width={option.photo.width}
                      height={option.photo.height}
                      loading="lazy"
                      decoding="async"
                      className={`w-full rounded-sm object-cover ${option.ratio}`}
                    />
                  </Link>

                  <h3 className="mt-6 font-display text-2xl text-forest-deep">
                    <Link to={routeTo(ROOMS_PATH)} className="link-underline">
                      {option.title}
                    </Link>
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {option.body}
                  </p>

                  <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                    <Link
                      to={routeTo(ROOMS_PATH)}
                      className="link-underline text-sm font-semibold text-forest"
                    >
                      Sites &amp; Rooms <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-underline text-sm font-semibold text-clay"
                    >
                      Check Availability <ArrowUpRight className="size-4" aria-hidden="true" />
                    </a>
                  </div>
                </article>
              </Reveal>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
